import React, { useState } from 'react';
import useProducts from '../hooks/useProducts';
import { useDiscountedProducts } from '../hooks/useDiscountedProducts'; // Import the new hook
import useCart from '../hooks/useCart';
import ProductModal from './ProductModal';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useTranslation, Trans } from 'react-i18next';
import { ScaleLoader } from 'react-spinners';


const Products = ({showToast}) => {
    const { t } = useTranslation();
    const { loading: regularLoading, error: regularError, products: regularProducts} = useProducts();
    const { discountedProducts, loading: discountLoading, error: discountError } = useDiscountedProducts();
    const { increment } = useCart();
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleModal = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedProduct(null);
    };

    const handleAddToCart = (productId, price) => {
        increment(productId, price, showToast);
    };

    // Fix: Handle both array and paginated response for regular products
    const getProductsArray = () => {
        if (Array.isArray(regularProducts)) return regularProducts;
        if (regularProducts && regularProducts.data) return regularProducts.data;
        return [];
    };

    const getNoDiscountProducts = () => {
        const productsArray = getProductsArray();
        return productsArray.filter(product => product.discount === 0 || !product.discount);
    };

    if (regularLoading || discountLoading) return <div className="text-center py-5">
        <ScaleLoader color="#7a2cc7" loading={true} />
    </div>;
    if (regularError) return <div className="text-center py-5">{t('Error:')} {regularError}</div>;
    if (discountError) return <div className="text-center py-5">{t('Error loading offers:')} {discountError}</div>;

    const noDiscountProducts = getNoDiscountProducts();

    return (
        <>
            {/* Special Offers */}
            <div className="offer-products container section-padding" id="products-offer-section">
                <div className="title mb-4 text-center">
                    <h2 className="fs-1 text-color-bright">{t('Special Offers')}</h2>
                    <p className="text-muted-color">
                        {t('explore our exclusive offer! discover special deals on top brands')}
                    </p>
                </div>
                <Swiper
                    modules={[Navigation]}
                    spaceBetween={16}
                    slidesPerView={3}
                    navigation
                    breakpoints={{
                        900: { slidesPerView: 3 },
                        1100: { slidesPerView: 4 },
                        1500: { slidesPerView: 5 }
                    }}
                    className="mySwiperOfferProducts"
                >
                    {discountedProducts.length > 0 ? (
                        discountedProducts.map(product => {
                            const discountedPrice = product.price - (product.price * product.discount) / 100;
                            return (
                                <SwiperSlide key={product.id}>
                                    <div className="card position-relative border-0">
                                        <div className="top-card">
                                            <h5 className="m-0 text-decoration-line-through">{product.price.toLocaleString()} IQD</h5>
                                            <button
                                                type="button"
                                                className="add-product m-0"
                                                onClick={() => handleAddToCart(product.id, discountedPrice)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="icon icon-tabler icon-tabler-shopping-bag-plus"
                                                    width="28"
                                                    height="28"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="#2c3e50"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M12.5 21h-3.926a3 3 0 0 1 -2.965 -2.544l-1.255 -8.152a2 2 0 0 1 1.977 -2.304h11.339a2 2 0 0 1 1.977 2.304l-.263 1.708" />
                                                    <path d="M16 19h6" />
                                                    <path d="M19 16v6" />
                                                    <path d="M9 11v-5a3 3 0 0 1 6 0v5" />
                                                </svg>
                                            </button>
                                        </div>
                                        <button className="product-link w-100 m-0 p-0 border-bottom-0" onClick={() => handleModal(product)}>
                                            <img
                                                src={`https://akadgatepharmacy.com/backend/storage/app/public${product.featuredImage.replace("/storage",'')}`}
                                                alt={product.name}
                                                height="180"
                                                width="180"
                                                loading="lazy"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/180x180?text=No+Image';
                                                }}
                                            />
                                        </button>
                                        <h5 className="offer-price">
                                            Only {discountedPrice.toLocaleString()} IQD
                                        </h5>
                                        <div className="bottom-card">
                                            <h5 className="mb-1">{product.name}</h5>
                                            <p className="m-0">{product.brand?.name || 'No Brand'}</p>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            );
                        })
                    ) : (
                        <div className="text-center w-100">
                            <h6>{t('No Special Offers Available.')}</h6>
                        </div>
                    )}
                </Swiper>
            </div>

            {/* Regular Products */}
            <div className="products container section-padding" id="products-section">
                <div className="title mb-4 text-center">
                    <h2 className="fs-1 text-color-bright">{t('Our Products')}</h2>
                    <p className="text-muted-color">
                        {t('more than 100+ products from best brands for you')}
                    </p>
                </div>
                <Swiper
                    modules={[Navigation]}
                    spaceBetween={16}
                    slidesPerView={2}
                    navigation
                    breakpoints={{
                        900: { slidesPerView: 3 },
                        1100: { slidesPerView: 4 },
                        1500: { slidesPerView: 5 }
                    }}
                    className="mySwiperProducts"
                >
                    {noDiscountProducts.length > 0 ? (
                        noDiscountProducts.map(product => (
                            <SwiperSlide key={product.id}>
                                <div className="card position-relative border-0">
                                    <div className="top-card">
                                        <h5 className="m-0">{product.price.toLocaleString()} IQD</h5>
                                        <button
                                            type="button"
                                            className="add-product m-0"
                                            onClick={() => handleAddToCart(product.id, product.price)}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="icon icon-tabler icon-tabler-shopping-bag-plus"
                                                width="28"
                                                height="28"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="#2c3e50"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M12.5 21h-3.926a3 3 0 0 1 -2.965 -2.544l-1.255 -8.152a2 2 0 0 1 1.977 -2.304h11.339a2 2 0 0 1 1.977 2.304l-.263 1.708" />
                                                <path d="M16 19h6" />
                                                <path d="M19 16v6" />
                                                <path d="M9 11v-5a3 3 0 0 1 6 0v5" />
                                            </svg>
                                        </button>
                                    </div>
                                    <button className="product-link w-100 m-0 p-0 border-0" onClick={() => handleModal(product)}>
                                        <img
                                            src={`https://akadgatepharmacy.com/backend/storage/app/public${product.featuredImage.replace("/storage",'')}`}
                                            alt={product.name}
                                            height="180"
                                            width="180"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/180x180?text=No+Image';
                                            }}
                                        />
                                    </button>
                                    <div className="bottom-card">
                                        <h6 className="mb-1 fw-semibold">{product.name}</h6>
                                        <p className="m-0 fw-light">{product.brand?.name || 'No Brand'}</p>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))
                    ) : (
                        <div className="text-center w-100">
                            <h6>{t('No Products Available.')}</h6>
                        </div>
                    )}
                </Swiper>
            </div>

            {/* Product Modal */}
            {showModal && (
                <ProductModal product={selectedProduct} showToast={showToast} onClose={closeModal} />
            )}
        </>
    );
};

export default Products;